import { useEffect, useState } from 'react';
import { clientScopedFetch } from '../lib/authedFetch';
import { useViewingAs } from '../hooks/useViewingAs';
import {
  GBP_TOPIC_OPTIONS, GBP_CTA_OPTIONS, GBP_WEEKDAYS, GBP_WEEK_OCCURRENCES, GBP_VIDEO_NOTE, gbpTrimNote,
} from '../lib/gbp';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

// Whether Google options should appear for the client being worked on:
// Google Business is connected AND open to this client.
//
// Reads /api/meta/status, the platform-flags route, rather than
// /api/meta-connections: the latter makes a live Instagram Graph call on every
// request for a follower count this has no use for. Keyed on the viewed
// client as well, for the usual Phase 3 reason — clientId is the signed-in
// user and does not change when an admin switches who they are viewing.
//
// null while loading, then true/false. A failed read is false: the Google
// option simply does not appear, which is the safe direction.
export function useGoogleBizConnected(clientId) {
  const { viewingAsClientId } = useViewingAs();
  // The answer is stored WITH the client it was for. During a switch (admin
  // enters or leaves a client view) the stored answer belongs to the previous
  // client, so this returns null until the new one lands — computed during
  // render, so no effect can act on the previous client's "enabled" (e.g. ask
  // a locked client's unread-review count) in the moment between.
  const key = `${clientId || ''}|${viewingAsClientId || ''}`;
  const [answer, setAnswer] = useState({ key: null, connected: null });
  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    clientScopedFetch(`${API_BASE}/api/meta/status`)
      .then(r => (r.ok ? r.json() : {}))
      // Connected AND open to this client. Google Business is admin-only until
      // Google verifies the app (server: gbp-access.js); a missing flag (an
      // older backend) reads as not enabled — the safe direction.
      .then(d => { if (!cancelled) setAnswer({ key, connected: !!d.google_business && d.google_business_enabled === true }); })
      .catch(() => { if (!cancelled) setAnswer({ key, connected: false }); });
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId, key]);
  return answer.key === key ? answer.connected : null;
}

// A single-colour "G" sized like the other platform icons in the pickers
// (13px, currentColor), so it follows the picker's on/off colour.
export function GoogleGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.24 10.29v3.6h5.09c-.22 1.3-1.55 3.82-5.09 3.82-3.06 0-5.56-2.54-5.56-5.67s2.5-5.67 5.56-5.67c1.74 0 2.91.74 3.58 1.38l2.44-2.35C16.69 3.95 14.66 3 12.24 3 7.27 3 3.24 7.03 3.24 12s4.03 9 9 9c5.2 0 8.64-3.65 8.64-8.8 0-.59-.06-1.04-.14-1.49h-8.5z"/>
    </svg>
  );
}

const card = { background:'var(--bg-card2)', border:'1px solid var(--accent-border)', borderRadius:10, padding:14, marginBottom:16 };
const sectionLabel = { fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6, marginTop:12 };
const input = {
  width:'100%', boxSizing:'border-box', padding:'8px 10px', borderRadius:8, fontSize:13,
  background:'var(--bg-surface)', border:'1px solid var(--border)', color:'var(--text-primary)', fontFamily:'var(--font)',
};
const row = { display:'flex', gap:8, flexWrap:'wrap' };
const pill = (on) => ({
  padding:'7px 12px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, border:'1px solid',
  background:  on ? 'var(--accent-dim)' : 'var(--bg-surface)',
  borderColor: on ? 'var(--accent-border)' : 'var(--border)',
  color:       on ? 'var(--accent)' : 'var(--text-muted)',
});

// The Google post options: type, button, offer/event details and repeat.
//
// Controlled: `value` is the full settings object (see lib/gbp.js
// defaultGbpSettings) and `onChange` receives the next one. `lockType` is set
// when editing a post already live on Google, where the type cannot change.
// `caption` (optional) is the post text Google will get; when it is over
// 1,500 characters after hashtags come off, the panel says Google will show
// the first 1,500. When editing a live post (showSummary), the Google-only
// text is what's measured instead.
export default function GoogleBusinessOptions({ value, onChange, isVideo = false, lockType = false, showSummary = false, summaryPlaceholder = '', caption = null }) {
  const s = value;
  const set = (patch) => onChange({ ...s, ...patch });
  const setIn = (key, patch) => onChange({ ...s, [key]: { ...(s[key] || {}), ...patch } });
  const type = s.topic_type || 'STANDARD';
  const hasEvent = type === 'EVENT' || type === 'OFFER';
  const label = type === 'OFFER' ? 'Offer' : 'Event';
  const rc = s.recurrence;

  if (isVideo) {
    return (
      <div style={{ ...card, borderColor:'rgba(245,166,35,0.3)' }} data-testid="gbp-video-note">
        <div style={{ fontSize:12, color:'#f5a623', lineHeight:1.6 }}>{GBP_VIDEO_NOTE}</div>
      </div>
    );
  }

  return (
    <div style={card} data-testid="gbp-options">
      <div style={{ fontSize:13, fontWeight:700, color:'var(--text-primary)' }}>Google Business</div>
      <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5, marginTop:2 }}>
        Hashtags are left off on Google. Carousels show their first photo.
      </div>
      {gbpTrimNote(showSummary ? s.summary : caption) && (
        <div style={{ fontSize:11, color:'#f5a623', lineHeight:1.5, marginTop:6 }} data-testid="gbp-trim-note">
          {gbpTrimNote(showSummary ? s.summary : caption)}.
        </div>
      )}

      <div style={sectionLabel}>Post type</div>
      <div style={row} role="radiogroup" aria-label="Google post type">
        {GBP_TOPIC_OPTIONS.map(o => (
          <button
            key={o.value} type="button" role="radio" aria-checked={type === o.value}
            disabled={lockType && type !== o.value}
            title={lockType && type !== o.value ? 'A live Google post can’t change type' : undefined}
            style={{ ...pill(type === o.value), opacity: lockType && type !== o.value ? 0.4 : 1 }}
            onClick={() => set({ topic_type: o.value, ...(o.value === 'STANDARD' ? { recurrence: null } : {}) })}
          >{o.label}</button>
        ))}
      </div>

      {showSummary && (
        <>
          <div style={sectionLabel}>Text on Google</div>
          <textarea
            aria-label="Text on Google"
            style={{ ...input, minHeight:80, resize:'vertical' }}
            value={s.summary || ''}
            placeholder={summaryPlaceholder}
            maxLength={1500}
            onChange={e => set({ summary: e.target.value })}
          />
        </>
      )}

      {hasEvent && (
        <>
          <div style={sectionLabel}>{label} title</div>
          <input aria-label={`${label} title`} style={input} value={s.event?.title || ''}
            placeholder={type === 'OFFER' ? '20% off all pastries' : 'Live music night'}
            onChange={e => setIn('event', { title: e.target.value })} />
          <div style={sectionLabel}>Starts</div>
          <div style={row}>
            <input aria-label="Start date" type="date" style={{ ...input, flex:'1 1 140px', width:'auto' }} value={s.event?.start_date || ''}
              onChange={e => setIn('event', { start_date: e.target.value })} />
            <input aria-label="Start time" type="time" style={{ ...input, flex:'1 1 110px', width:'auto' }} value={s.event?.start_time || ''}
              onChange={e => setIn('event', { start_time: e.target.value })} />
          </div>
          <div style={sectionLabel}>Ends</div>
          <div style={row}>
            <input aria-label="End date" type="date" style={{ ...input, flex:'1 1 140px', width:'auto' }} value={s.event?.end_date || ''}
              onChange={e => setIn('event', { end_date: e.target.value })} />
            <input aria-label="End time" type="time" style={{ ...input, flex:'1 1 110px', width:'auto' }} value={s.event?.end_time || ''}
              onChange={e => setIn('event', { end_time: e.target.value })} />
          </div>
          <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Leave the times blank for all day.</div>
        </>
      )}

      {type === 'OFFER' && (
        <>
          <div style={sectionLabel}>Coupon code (optional)</div>
          <input aria-label="Coupon code" style={input} value={s.offer?.coupon_code || ''} placeholder="SAVE20"
            onChange={e => setIn('offer', { coupon_code: e.target.value })} />
          <div style={sectionLabel}>Terms (optional)</div>
          <textarea aria-label="Terms" style={{ ...input, minHeight:60, resize:'vertical' }} value={s.offer?.terms || ''}
            placeholder="One per customer. Not valid with other offers."
            onChange={e => setIn('offer', { terms: e.target.value })} />
          <div style={sectionLabel}>Redeem online link (optional)</div>
          <input aria-label="Redeem link" style={input} value={s.offer?.redeem_url || ''} placeholder="https://"
            onChange={e => setIn('offer', { redeem_url: e.target.value })} />
        </>
      )}

      <div style={sectionLabel}>Button</div>
      <select aria-label="Google button" style={input} value={s.cta?.action_type || ''}
        onChange={e => setIn('cta', { action_type: e.target.value })}>
        {GBP_CTA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {s.cta?.action_type && s.cta.action_type !== 'CALL' && (
        <input aria-label="Button link" style={{ ...input, marginTop:8 }} value={s.cta?.url || ''} placeholder="https://"
          onChange={e => setIn('cta', { url: e.target.value })} />
      )}
      {s.cta?.action_type === 'CALL' && (
        <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Calls the phone number on your Google listing.</div>
      )}

      {hasEvent && (
        <>
          <div style={sectionLabel}>Repeat</div>
          <select aria-label="Repeat" style={input} value={rc?.pattern || ''}
            onChange={e => {
              const pattern = e.target.value;
              if (!pattern) return set({ recurrence: null });
              set({ recurrence: {
                pattern,
                days_of_week: pattern === 'weekly' ? (rc?.days_of_week || []) : undefined,
                day_of_month: pattern === 'monthly' ? (rc?.day_of_month || 1) : undefined,
                series_end_date: rc?.series_end_date || null,
              } });
            }}>
            <option value="">Doesn’t repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>

          {rc?.pattern === 'weekly' && (
            <div style={{ ...row, marginTop:8 }}>
              {GBP_WEEKDAYS.map(([v, l]) => {
                const on = (rc.days_of_week || []).includes(v);
                return (
                  <button key={v} type="button" aria-pressed={on} style={pill(on)}
                    onClick={() => set({ recurrence: { ...rc, days_of_week: on ? rc.days_of_week.filter(d => d !== v) : [...(rc.days_of_week || []), v] } })}
                  >{l}</button>
                );
              })}
              <div style={{ fontSize:11, color:'var(--text-muted)', width:'100%' }}>None picked repeats on the start date’s weekday.</div>
            </div>
          )}

          {rc?.pattern === 'monthly' && (
            <div style={{ ...row, marginTop:8, alignItems:'center' }}>
              <select aria-label="Monthly repeat" style={{ ...input, flex:'1 1 160px', width:'auto' }}
                value={rc.week_occurrence ? `w:${rc.week_occurrence}` : 'd'}
                onChange={e => {
                  const v = e.target.value;
                  set({ recurrence: v === 'd'
                    ? { pattern: 'monthly', day_of_month: rc.day_of_month || 1, series_end_date: rc.series_end_date || null }
                    : { pattern: 'monthly', week_occurrence: v.slice(2), series_end_date: rc.series_end_date || null } });
                }}>
                <option value="d">On day of the month</option>
                {GBP_WEEK_OCCURRENCES.map(([v, l]) => <option key={v} value={`w:${v}`}>{l} week (same weekday as start)</option>)}
              </select>
              {!rc.week_occurrence && (
                <input aria-label="Day of month" type="number" min={1} max={31} style={{ ...input, flex:'0 0 80px', width:80 }}
                  value={rc.day_of_month || ''} onChange={e => set({ recurrence: { ...rc, day_of_month: Number(e.target.value) } })} />
              )}
            </div>
          )}

          {rc?.pattern && (
            <>
              {/* Required: Google refuses a repeat without one (confirmed
                  live Oct 4, although its docs call it optional). */}
              <div style={sectionLabel}>Stop repeating on (required)</div>
              <input aria-label="Repeat end date" type="date" required aria-required="true"
                style={{ ...input, ...(rc.series_end_date ? {} : { borderColor:'rgba(245,166,35,0.6)' }) }}
                value={rc.series_end_date || ''} min={s.event?.start_date || undefined}
                onChange={e => set({ recurrence: { ...rc, series_end_date: e.target.value || null } })} />
              {!rc.series_end_date && (
                <div style={{ fontSize:11, color:'#f5a623', marginTop:4 }} data-testid="repeat-end-missing">
                  Google needs a date to stop repeating.
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
